import React, { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useAuth } from '../context/AuthContext';
import { useT } from '../i18n';
import { useChats } from '../hooks/useChat';
import { sendMessage, MESSAGE_MAX } from '../firebase/chatService';
import { compressShareThumb } from '../utils/imageCompress';
import { otherParticipant } from './ChatList';
import PersonIcon from './PersonIcon';
import PageSpinner from './PageSpinner';
import './ShareToChat.css';

// ── THE ICON THAT OPENS THIS ─────────────────────────────────────────────────
// Lives beside the picker rather than being inlined at each call site, because
// there are two of those — the Share modal and the gallery lightbox — and both
// already import this file. Two hand-copied paths is how one of them ends up a
// different glyph after a later edit.
//
// A PAPER PLANE, not a speech bubble: the app already uses a bubble for
// Messages itself, and this is the act of sending rather than a place to read.
// Stroke-only and unsized, so it inherits the colour and scales with the button
// it sits in, like every other icon in the chat components.
export function SendIcon({ size = 14 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="m22 2-7 20-4-9-9-4Z" />
      <path d="M22 2 11 13" />
    </svg>
  );
}

// ── SEND THIS TO SOMEONE ─────────────────────────────────────────────────────
// Picks a conversation and drops the work into it as a share card — the other
// half of `chatService.sendMessage`'s `share` option.
//
// ONE COMPONENT FOR EVERY ENTRY POINT. The Share modal, the gallery tile menu
// and the lightbox all open this with the same `{ kind, ownerUid, refId, title }`
// and a thumbnail source; nothing about picking a conversation differs between
// them, and three copies of a list with a send button is how they would come to
// differ.
//
// PORTALLED TO THE BODY. It opens from inside other modals — the Share dialog
// sits at z-index 4000, the lightbox at 1600 — each of which has its own
// stacking context and its own close-on-backdrop-click. Rendered in place it
// would be trapped under one and dismissed by the other.
//
// IT SENDS TO EXISTING CONVERSATIONS ONLY, and that is deliberate rather than a
// missing feature: the chat list is a single bounded query this hook already
// runs, whereas "pick anyone" means searching users, and a message from a
// stranger carrying a link is precisely the shape of thing nobody wants. To
// share with someone new, open a conversation with them first — the profile's
// Message button already does that.
export default function ShareToChat({ share, thumbSrc = '', onClose }) {
  const { user } = useAuth();
  const { t } = useT();
  const { chats, loading } = useChats();

  const [caption, setCaption] = useState('');
  const [thumb, setThumb] = useState('');
  const [busyId, setBusyId] = useState('');
  // Chat ids this share has already gone to, so a row cannot be double-sent by
  // an impatient second tap and the modal shows what it has done.
  const [sentTo, setSentTo] = useState([]);
  const [failed, setFailed] = useState('');
  // The word a refused caption tripped on, so the error can name it.
  const [blockedWord, setBlockedWord] = useState('');

  // ── THE PICTURE, RE-ENCODED ONCE ───────────────────────────────────────────
  // `thumbSrc` is a gallery tile's 640px thumbnail; the card draws it at 52.
  // Doing this here rather than at send time means the cost is paid once even
  // when the same share goes to three people, and it is paid while the user is
  // choosing a conversation rather than after they have tapped Send.
  //
  // A failure is not one: compressShareThumb returns '' and the card falls back
  // to its placeholder glyph.
  useEffect(() => {
    let alive = true;
    if (!thumbSrc) { setThumb(''); return undefined; }
    compressShareThumb(thumbSrc).then(url => { if (alive) setThumb(url); });
    return () => { alive = false; };
  }, [thumbSrc]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Most-recent first is what the hook already returns; this only drops the
  // conversation with yourself, which cannot exist, and any row whose other
  // participant could not be resolved.
  const rows = useMemo(
    () => chats
      .map(c => ({ chat: c, other: otherParticipant(c, user?.uid) }))
      .filter(r => r.other?.uid),
    [chats, user?.uid],
  );

  async function send(chatId, toUid) {
    if (busyId || sentTo.includes(chatId)) return;
    setBusyId(chatId);
    setFailed('');
    setBlockedWord('');
    try {
      await sendMessage(chatId, user.uid, toUid, caption, {
        share: { ...share, thumb },
      });
      setSentTo(prev => [...prev, chatId]);
    } catch (err) {
      // A refused CAPTION is not a failure to send — it is a thing the sender
      // can fix, and "could not send" would have them retrying the same words.
      // Named separately for that reason; everything else keeps the generic
      // line. See sendMessage.
      if (err?.code === 'profanity') {
        setBlockedWord(err.word || '');
      } else {
        console.error('[share] could not send:', err);
      }
      setFailed(chatId);
    } finally {
      setBusyId('');
    }
  }

  return createPortal(
    <div className="stc" role="dialog" aria-modal="true" onClick={onClose}>
      <div className="stc-box" onClick={e => e.stopPropagation()}>
        <header className="stc-head">
          <h3 className="stc-title">{t('share_to_chat')}</h3>
          <button type="button" className="stc-x" onClick={onClose} aria-label={t('cancel')}>✕</button>
        </header>

        {/* WHAT IS ABOUT TO BE SENT, drawn the way it will arrive. The same
            argument as the composer's pending-picture strip: an attachment you
            cannot see until after it has gone is one you cannot check. */}
        <div className="stc-preview">
          <span className="stc-preview-thumb">
            {thumb ? <img src={thumb} alt="" /> : <span className="stc-preview-glyph" aria-hidden="true">🏠</span>}
          </span>
          <span className="stc-preview-title">{share.title || t('share_msg_untitled')}</span>
        </div>

        <input
          className="stc-caption"
          value={caption}
          onChange={e => setCaption(e.target.value.slice(0, MESSAGE_MAX))}
          placeholder={t('share_to_chat_note')}
          maxLength={MESSAGE_MAX}
        />

        {loading ? (
          <PageSpinner label={t('chat_loading')} />
        ) : !rows.length ? (
          <p className="stc-empty">{t('share_to_chat_none')}</p>
        ) : (
          <ul className="stc-rows">
            {rows.map(({ chat, other }) => {
              const sent = sentTo.includes(chat.id);
              return (
                <li key={chat.id} className="stc-li">
                  <span className="stc-avatar">
                    {other.avatar ? <img src={other.avatar} alt="" /> : <PersonIcon />}
                  </span>
                  <span className="stc-name">{other.name || t('chat_someone')}</span>
                  <button
                    type="button"
                    className={`stc-send${sent ? ' stc-send--done' : ''}`}
                    onClick={() => send(chat.id, other.uid)}
                    disabled={sent || !!busyId}
                  >
                    {sent ? t('share_to_chat_sent')
                      : busyId === chat.id ? '…'
                        : t('chat_send')}
                  </button>
                </li>
              );
            })}
          </ul>
        )}

        {failed && (
          <p className="stc-err">
            {blockedWord
              ? t('chat_blocked_word').replace('{word}', blockedWord)
              : t('share_to_chat_error')}
          </p>
        )}
      </div>
    </div>,
    document.body,
  );
}
